import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { PhoneCards } from './PhoneCards';
import { useNarrowViewport } from './useNarrow';

/**
 * DataTable — the desk's table (deskkit.dtable / rawtable / selrow).
 *
 * Two rules from the handoff are enforced here rather than left to callers:
 *
 *  - §13.3: "DataTables must state their truncation." A table that claims more
 *    rows than it draws MUST say so. The Accountant shipped a 115-bill range
 *    over three rows plus a dead `… | 21 more rows` filler. So `total` is part
 *    of the API, and a `more` row renders whenever it exceeds what is drawn.
 *  - §13.1: the whole row is the hit box, 44px. Selection drives the pane
 *    (§13.5), which is why `onSelect` gives you the row rather than an index.
 *
 * Responsive: the table scrolls inside its own container, never the page body.
 *
 * BELOW THE DESK BREAKPOINT IT IS NOT A TABLE. A five-column table at 375px is
 * not a narrower desk, it is an unusable one — the last column clips off the
 * edge and the first two wrap to four lines each. This used to be left to the
 * caller ("a caller should generally render a card list instead"), and of the
 * seventy-odd tables in the app a handful ever did. So the table draws
 * `PhoneCards` itself, from its own columns and rows: one row, one card, the
 * same cells in a different box.
 *
 * A PAGE THAT DRAWS ITS OWN PHONE BOARD STILL WINS, because the shell renders
 * `chrome.phone` instead of the desk body — that board's own PhoneCards names
 * which column is the title and which is the chip, which this cannot know. The
 * default is the honest one: first column as the body, last as the trailing
 * chip, `secondary` columns dropped, everything else stacked underneath.
 */

export interface Column {
  key: string;
  label: ReactNode;
  /** Percent. The PRIMARY column gets >= 45% (§5). */
  width?: number;
  /** Hidden below the desk breakpoint when the table must stay a table. */
  secondary?: boolean;
  /**
   * What this column becomes on a phone, where the table is a list of cards.
   *
   * The default — first column as the card's body, last as the trailing chip —
   * is right for most tables and wrong for the ones whose last column is prose
   * rather than a state. Saying so here keeps the answer next to the column it
   * is about, instead of in a second phone-only copy of the same table.
   */
  role?: 'body' | 'trailing';
}

/**
 * One table row's DATA. Named `TableRow` and not `Row` on purpose: `Row` is the
 * kit's list-row COMPONENT (Controls.tsx). Exporting both under one name from
 * the barrel resolved to the type, so `<Row …>` stopped being a value and five
 * phone pages broke at once.
 */
export interface TableRow {
  id: string;
  cells: Record<string, ReactNode>;
  /**
   * `nested` tints the row and is a STRUCTURAL claim, not decoration: it says
   * this row belongs to the one above it. The Nurse's day is the case — the
   * dilation task owns "Sara M. — dilating" and "Karim F. — 2 now-tasks" under
   * it, which are its consequences rather than separate work, and the canvas
   * carries that as a beige fill with the Lane cell left empty.
   */
  tone?: 'nested';
  /**
   * Turns the row into a full-width SECTION HEADING inside the table, and
   * `cells` is ignored. Reception's queue is the case: one table carrying
   * "Standby · 2 — today, in the room" and "Booked, not arrived · 2" as
   * headings between the rows, rather than three tables under three titles.
   *
   * That matters for what the desk can do with it — a patient promoted off
   * standby moves UP the same list, and three tables would have had to move a
   * row between them.
   */
  section?: ReactNode;
  /** A summary line (a total) — not selectable, not drawn as a control on desk or phone. */
  inert?: boolean;
}

interface DataTableProps {
  columns: Column[];
  rows: TableRow[];
  selectedId?: string | null;
  onSelect?: (row: TableRow) => void;
  /**
   * Total rows the surface represents. Required whenever it can exceed
   * `rows.length` — the truncation notice is not optional (§13.3).
   */
  total?: number;
  onLoadMore?: () => void;
  /** Rendered when there are no rows at all. */
  empty?: ReactNode;
  caption?: string;
  className?: string;
  /** On a phone, label each stacked value with its column heading. */
  phoneLabels?: boolean;
}

export function DataTable({
  columns,
  rows,
  selectedId,
  onSelect,
  total,
  onLoadMore,
  empty,
  caption,
  className,
  phoneLabels,
}: DataTableProps) {
  const narrow = useNarrowViewport();
  if (!rows.length && empty) return <>{empty}</>;

  const truncated = typeof total === 'number' && total > rows.length;

  if (narrow) {
    return (
      <div className={clsx('dtable-cards', className)}>
        <PhoneCards
          columns={columns}
          rows={rows}
          body={columns.find((c) => c.role === 'body')?.key}
          trailing={columns.find((c) => c.role === 'trailing')?.key}
          /* `secondary` already means "drop this when the table must stay a
             table" — a card is that case taken to its end, so the same
             columns go. */
          hide={columns.filter((c) => c.secondary).map((c) => c.key)}
          onOpen={onSelect}
          labelMiddle={phoneLabels}
          cardStyle={(row) =>
            row.id === selectedId ? { borderColor: 'var(--brand-500)' } : undefined
          }
        />
        {truncated ? (
          <div className="dt-more">
            showing {rows.length} of {total}
            {onLoadMore ? (
              <>
                {' · '}
                <button type="button" className="lnk" onClick={onLoadMore}>
                  Load more
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={clsx('dtable-scroll', className)}>
      <table className="dtable">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: `${col.width}%` } : undefined}
                className={clsx(col.secondary && 'kit-col-secondary')}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.section !== undefined) {
              return (
                <tr key={row.id} className="dt-section">
                  <td colSpan={columns.length}>
                    <p className="t-ovl mut">{row.section}</p>
                  </td>
                </tr>
              );
            }
            const selected = row.id === selectedId;
            return (
              <tr
                key={row.id}
                className={clsx('rowc', selected && 'sel', row.tone === 'nested' && 'nested')}
                onClick={onSelect && !row.inert ? () => onSelect(row) : undefined}
                aria-selected={onSelect && !row.inert ? selected : undefined}
                tabIndex={onSelect && !row.inert ? 0 : undefined}
                onKeyDown={
                  onSelect && !row.inert
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelect(row);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <td key={col.key} className={clsx(col.secondary && 'kit-col-secondary')}>
                    {row.cells[col.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {truncated ? (
        <div className="dt-more">
          showing {rows.length} of {total}
          {onLoadMore ? (
            <>
              {' · '}
              <button type="button" className="lnk" onClick={onLoadMore}>
                Load more
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
